import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import { getBlogPost, getRelatedPosts } from "@/data/blogPosts";
import { ArrowLeft, ArrowUpRight, Clock, User } from "lucide-react";
import gsap from "gsap";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  useEffect(() => {
    if (!post) return;
    window.scrollTo({ top: 0, behavior: "instant" });
    const ctx = gsap.context(() => {
      gsap.from(".blog-anim", {
        opacity: 0,
        y: 30,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.08,
      });
    });
    return () => ctx.revert();
  }, [slug, post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post.slug, post.category, 3);

  // Split body into paragraphs by sentences for better readability.
  const paragraphs = post.body.split(/(?<=\. )(?=[A-Z])/);
  const mid = Math.ceil(paragraphs.length / 2);
  const firstHalf = paragraphs.slice(0, mid).join("");
  const secondHalf = paragraphs.slice(mid).join("");

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title={post.title}
          description={post.excerpt}
          image={post.image}
          type="article"
        />
        <Navbar transparent />

        {/* Hero */}
        <section className="relative h-[80vh] min-h-[560px] w-full overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FDFBF7]" />
          <div className="relative z-10 h-full max-w-5xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
            <div className="blog-anim t-overline text-white/85 mb-4">{post.category}</div>
            <h1 className="blog-anim text-white font-light leading-[0.95] tracking-tight max-w-4xl"
                style={{ fontSize: "clamp(2.25rem, 5.8vw, 4.5rem)" }}>
              <b className="font-bold">{post.title}</b>
            </h1>
            <div className="blog-anim mt-7 flex flex-wrap items-center gap-5 text-xs uppercase tracking-[0.2em] text-white/80">
              <div className="flex items-center gap-2">
                <User size={13} strokeWidth={1.5} /> {post.author}
              </div>
              <div className="flex items-center gap-2">
                <Clock size={13} strokeWidth={1.5} /> {post.readTime}
              </div>
              <div className="opacity-70">
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Back link */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 mt-10">
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={1.5} /> All stories
          </Link>
        </div>

        {/* Body */}
        <article className="max-w-3xl mx-auto px-6 md:px-12 mt-12 pb-20">
          <p className="text-2xl font-light leading-snug text-slate-900 tracking-tight blog-anim">
            {post.excerpt}
          </p>

          <div className="mt-12 prose prose-lg max-w-none">
            <p className="text-[1.07rem] leading-[1.85] text-slate-700 blog-anim">
              {firstHalf}
            </p>
            <div className="my-12 flex items-center gap-4 blog-anim">
              <div className="flex-1 h-px bg-stone-200" />
              <span className="text-seafoam text-3xl font-script">~</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
            {secondHalf && (
              <p className="text-[1.07rem] leading-[1.85] text-slate-700 blog-anim">
                {secondHalf}
              </p>
            )}
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 md:p-10 rounded-3xl bg-white border border-stone-100 shadow-sm blog-anim">
            <div className="t-overline text-seafoam mb-3">Plan your journey</div>
            <h3 className="text-2xl md:text-3xl font-light tracking-tight text-slate-900">
              Want a private itinerary inspired by this story?
            </h3>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
              Our concierge designs slow, considered journeys for international
              travelers visiting Indonesia. Book a free 30-minute consultation —
              we will sketch a rough route, in your time zone.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/consultations"
                className="btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm shadow-md"
              >
                Book a consultation
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </Link>
              <Link
                to="/tours"
                className="btn-pill bg-white border border-slate-200 text-slate-900 hover:bg-stone-50 text-sm"
              >
                Explore tours
              </Link>
            </div>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="t-overline text-seafoam">Continue reading</div>
                <h3 className="mt-2 text-3xl md:text-4xl font-light tracking-tight text-slate-900">
                  More from <b className="font-bold">{post.category}</b>
                </h3>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                All stories <ArrowUpRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-stone-100">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                  </div>
                  <h4 className="mt-4 text-lg font-light leading-snug text-slate-900 group-hover:text-seafoam transition-colors">
                    <b className="font-bold">{r.title.split(":")[0]}</b>
                  </h4>
                  <div className="mt-2 text-[11px] uppercase tracking-wider text-slate-400">
                    {r.readTime}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <Footer />
      </div>
    </SmoothScroll>
  );
}
